import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { ArrowBack, CardMembership, MailOutline, Refresh, Save } from '@mui/icons-material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { toast } from 'react-toastify';
import {
  fetchPlans,
  fetchLookups,
  fetchTenantSubscription,
  fetchSubscriptionById,
  fetchTenants,
  provisionTenantLicense,
  updateSubscriptionProducts,
  updateSubscriptionSettings,
  replaceSubscriptionLlms,
  upsertEntitlement,
  emptyOptionalExtras,
  extrasFromEntitlements,
  OPTIONAL_ENTITLEMENT_SPECS,
  USAGE_TYPE_LABELS,
  productsFromLookups,
  featureFlagOptionsFromLookups,
  fetchLicenseConnectorCatalog,
  fetchLicenseFlowCatalog,
  fetchLicenseLlmCatalog,
  replaceLicensedConnectors,
  replaceLicensedFlows,
  isSubscriptionStatusOperable,
  subscriptionLockMessage,
} from '../services/licensingApi';
import {
  ensureMasterMenus,
  fetchAvailableTenantMenus,
  saveTenantMenus,
} from '../services/rolesMenuApi';
import OptionalExtrasFields from './OptionalExtrasFields';
import LicenseProductsMenusGrid, {
  isSharedMenu,
  menuKey,
  productOf,
} from './LicenseProductsMenusGrid';
import LicenseIntegrationAssets from './LicenseIntegrationAssets';
import LicenseLlmAssets from './LicenseLlmAssets';
import ProductCostGrid from './Billing/ProductCostGrid';
import { fetchInvoices, fetchTenantProductGrid, formatMoney, sendInvoice } from '../services/billingApi';
import {
  flowUiCatalogAllFromLookups,
  flowUiCatalogFromLookups,
  parseMasterFlowUiConfig,
} from '../services/masterFlowUiConfig';

/** Always show these; hide unused meters (API/storage) until used > 0. */
const ALWAYS_SHOW_USAGE = new Set([
  'USERS',
  'CONNECTORS',
  'FLOWS',
  'AI_AGENTS',
  'AI_TOKENS',
  'MCP_SERVERS',
  'AGENT_TEAMS',
  'API_CALLS',
  'STORAGE_GB',
  'RAG_STORAGE_GB',
]);

const isCdxUser = () => {
  try {
    return localStorage.getItem('cdx_user') === 'true';
  } catch {
    return false;
  }
};

const getDefaultTenantId = (searchTenantId) => {
  if (searchTenantId) return String(searchTenantId);
  const tid = localStorage.getItem('tenantId');
  if (!tid || tid === 'ALL' || tid === 'null' || tid === 'undefined') return '';
  return String(tid);
};

const formatQty = (v) => {
  if (v == null || v === '') return '—';
  const n = Number(v);
  if (!Number.isFinite(n)) return String(v);
  return Number.isInteger(n) ? String(n) : n.toLocaleString();
};

/**
 * CDX License view — edit plan products/entitlements/usage for an existing tenant.
 */
const LicenseAdmin = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [tenants, setTenants] = useState([]);
  const [plans, setPlans] = useState([]);
  const [lookups, setLookups] = useState({});
  const [tenantId, setTenantId] = useState(() => getDefaultTenantId(searchParams.get('tenantId')));
  const [subscriptionIdParam, setSubscriptionIdParam] = useState(() => searchParams.get('subscriptionId') || '');
  const [subscription, setSubscription] = useState(null);
  const [productState, setProductState] = useState({});
  const [menus, setMenus] = useState([]);
  const [selectedMenuIds, setSelectedMenuIds] = useState(() => new Set());
  const [connectorCatalog, setConnectorCatalog] = useState([]);
  const [flowCatalog, setFlowCatalog] = useState([]);
  const [llmCatalog, setLlmCatalog] = useState([]);
  const [licensedConnectorIds, setLicensedConnectorIds] = useState(() => new Set());
  const [licensedFlowIds, setLicensedFlowIds] = useState(() => new Set());
  /** mfId → screen UI config for this subscription's licensed flows */
  const [licensedFlowUiByMfId, setLicensedFlowUiByMfId] = useState({});
  const [settingsForm, setSettingsForm] = useState({
    subscriptionStatus: 'ACTIVE',
    autoRenew: true,
    gracePeriodDays: '',
    currency: 'USD',
    billingCycle: 'MONTHLY',
    overagePolicy: 'BLOCK',
    startDate: '',
    endDate: '',
  });
  const [llmDraft, setLlmDraft] = useState([]);
  const [extrasForm, setExtrasForm] = useState(emptyOptionalExtras);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [sendOpen, setSendOpen] = useState(false);
  const [sendConfirmText, setSendConfirmText] = useState('');
  const [sendBusy, setSendBusy] = useState(false);
  const [invoiceToSend, setInvoiceToSend] = useState(null);
  const [productCostGrid, setProductCostGrid] = useState(null);
  const [provisionForm, setProvisionForm] = useState({
    planName: 'Trial',
    subscription: 'MONTHLY',
    numberOfUsers: 5,
  });

  useEffect(() => {
    const q = searchParams.get('tenantId');
    if (q) setTenantId(String(q));
    setSubscriptionIdParam(searchParams.get('subscriptionId') || '');
  }, [searchParams]);

  const selectedTenant = useMemo(
    () => tenants.find((t) => String(t.tenantId) === String(tenantId)),
    [tenants, tenantId]
  );

  const isHistorical = subscription != null && subscription.current === false;
  const contentLocked =
    subscription != null &&
    !isSubscriptionStatusOperable(subscription.subscriptionStatus);

  const usageRows = useMemo(() => {
    const rows = subscription?.usage || [];
    return rows.filter((u) => {
      const type = String(u.usageType || '').toUpperCase();
      if (ALWAYS_SHOW_USAGE.has(type)) return true;
      const used = Number(u.usedQuantity);
      return Number.isFinite(used) && used > 0;
    });
  }, [subscription]);

  const billingOptions = lookups.BILLING_CYCLE || [];
  const productOptions = productsFromLookups(lookups);
  const featureFlagOptions = featureFlagOptionsFromLookups(lookups, { allowBlank: false });

  const loadTenantsAndPlans = useCallback(async () => {
    try {
      const [t, p, lu] = await Promise.all([
        fetchTenants(),
        fetchPlans(),
        fetchLookups({ grouped: true }),
      ]);
      setTenants(t);
      setPlans(Array.isArray(p) ? p : []);
      setLookups(lu && typeof lu === 'object' ? lu : {});
      if (!tenantId && t.length > 0) {
        setTenantId(String(t[0].tenantId));
      }
    } catch (e) {
      console.error(e);
      toast.error(e.message || 'Failed to load tenants');
    }
  }, [tenantId]);

  const applySubscription = (sub, productsList = [], lookupMap = {}) => {
    setSubscription(sub);
    const map = {};
    (productsList || []).forEach((p) => {
      map[p.code] = false;
    });
    (sub?.products || []).forEach((p) => {
      map[p.productCode] = !!p.enabled;
    });
    setProductState(map);
    setSettingsForm({
      subscriptionStatus: sub?.subscriptionStatus || 'ACTIVE',
      autoRenew: sub?.autoRenew !== false,
      gracePeriodDays: sub?.gracePeriodDays != null ? sub.gracePeriodDays : '',
      currency: sub?.currency || 'USD',
      billingCycle: sub?.billingCycle || 'MONTHLY',
      overagePolicy: sub?.overagePolicy || 'BLOCK',
      startDate: sub?.startDate || '',
      endDate: sub?.endDate || '',
    });
    setLlmDraft(
      (sub?.llms || []).map((l) => ({
        masterLlmId: l.masterLlmId || l.master_llm_id || null,
        provider: l.provider || 'OpenAI',
        modelCode: l.modelCode || '',
        displayName: l.displayName || l.display_name || '',
        enabled: l.enabled !== false,
        monthlyTokenLimit: l.monthlyTokenLimit ?? 2000000,
      }))
    );
    setExtrasForm(extrasFromEntitlements(sub?.entitlements));
    setLicensedConnectorIds(
      new Set(
        (sub?.licensedConnectors || [])
          .map((c) => String(c.mcId || c.mc_id || ''))
          .filter(Boolean)
      )
    );
    setLicensedFlowIds(
      new Set(
        (sub?.licensedFlows || [])
          .map((f) => String(f.mfId || f.mf_id || ''))
          .filter(Boolean)
      )
    );
    const uiMap = {};
    const allCatalog = flowUiCatalogAllFromLookups(lookupMap);
    (sub?.licensedFlows || []).forEach((f) => {
      const id = String(f.mfId || f.mf_id || '');
      if (!id) return;
      const path = f.mfGroup || f.mf_flow_function || f.mfFlowFunction || '';
      const catalog = flowUiCatalogFromLookups(lookupMap, path);
      const raw = f.screenUi ?? f.screen_ui;
      if (raw != null && String(raw).trim() !== '') {
        uiMap[id] = parseMasterFlowUiConfig(
          raw,
          catalog.actions.length || catalog.sections.length ? catalog : allCatalog
        );
      }
    });
    setLicensedFlowUiByMfId(uiMap);
  };

  const loadSubscription = useCallback(async (tid, subId) => {
    if (!tid) {
      setSubscription(null);
      setMenus([]);
      setSelectedMenuIds(new Set());
      setProductCostGrid(null);
      return;
    }
    setLoading(true);
    try {
      const [sub, lu] = await Promise.all([
        subId ? fetchSubscriptionById(subId) : fetchTenantSubscription(tid),
        fetchLookups({ grouped: true }),
      ]);
      setLookups(lu && typeof lu === 'object' ? lu : {});
      if (sub && subId && String(sub.tenantId) !== String(tid)) {
        toast.error('Subscription does not belong to this tenant');
        setSubscription(null);
        return;
      }
      applySubscription(sub, productsFromLookups(lu), lu && typeof lu === 'object' ? lu : {});
      if (!sub) {
        toast.info('No license yet for this tenant — use Provision');
        setProductCostGrid(null);
      } else {
        try {
          const g = await fetchTenantProductGrid(tid, sub.subscriptionId);
          setProductCostGrid(g);
        } catch (gridErr) {
          console.warn('Product cost grid skipped:', gridErr);
          setProductCostGrid(null);
        }
      }

      try {
        const [connCat, flowCat, llmCat] = await Promise.all([
          fetchLicenseConnectorCatalog().catch(() => []),
          fetchLicenseFlowCatalog().catch(() => []),
          fetchLicenseLlmCatalog().catch(() => []),
        ]);
        setConnectorCatalog(
          (connCat || []).map((c) => ({
            mcId: String(c.mc_id || c.mcId || ''),
            mcName: c.mc_name || c.mcName || '',
            mcConnectorType: c.mc_connector_type || c.mcConnectorType || '',
          }))
        );
        setFlowCatalog(
          (flowCat || []).map((f) => ({
            mfId: String(f.mf_id || f.mfId || ''),
            mfName: f.mf_name || f.mfName || '',
            mfFlowFunction: f.mf_flow_function || f.mfFlowFunction || '',
          }))
        );
        setLlmCatalog(
          (llmCat || []).map((m) => ({
            masterLlmId: String(m.master_llm_id || m.masterLlmId || ''),
            provider: m.provider || '',
            modelCode: m.model_code || m.modelCode || '',
            displayName: m.display_name || m.displayName || '',
            invocationKind: m.invocation_kind || m.invocationKind || '',
          }))
        );
      } catch (catErr) {
        console.warn('Catalog load skipped:', catErr);
      }

      try {
        await ensureMasterMenus().catch(() => null);
        const available = await fetchAvailableTenantMenus(tid, { includePlatformOnly: false });
        setMenus(available);
        const enabledProducts = new Set(
          (sub?.products || [])
            .filter((p) => p.enabled !== false)
            .map((p) => String(p.productCode || p.product_code || '').toUpperCase())
            .filter(Boolean)
        );
        const initial = new Set();
        available.forEach((m) => {
          const assigned = m.assigned === true || m.enabled === true;
          if (!assigned) return;
          if (isSharedMenu(m)) {
            initial.add(menuKey(m));
            return;
          }
          const code = productOf(m);
          if (enabledProducts.has(code)) initial.add(menuKey(m));
        });
        setSelectedMenuIds(initial);
      } catch (menuErr) {
        console.warn('Menus load skipped:', menuErr);
        setMenus([]);
        setSelectedMenuIds(new Set());
      }
    } catch (e) {
      console.error(e);
      toast.error(e.message || 'Failed to load subscription');
      setSubscription(null);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleProductChange = (code, enabled) => {
    setProductState((s) => ({ ...s, [code]: enabled }));
    const productMenus = (menus || []).filter((m) => !isSharedMenu(m) && productOf(m) === code);
    setSelectedMenuIds((prev) => {
      const next = new Set(prev);
      if (enabled) {
        productMenus.forEach((m) => next.add(menuKey(m)));
      } else {
        productMenus.forEach((m) => next.delete(menuKey(m)));
      }
      return next;
    });
  };

  const handleMenuToggle = (id) => {
    setSelectedMenuIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleToggleProductMenus = (code, productMenus, checked) => {
    setSelectedMenuIds((prev) => {
      const next = new Set(prev);
      productMenus.forEach((m) => {
        const id = menuKey(m);
        if (checked) next.add(id);
        else next.delete(id);
      });
      return next;
    });
  };

  const buildMenuIdsToSave = () => {
    const ids = new Set();
    (menus || []).forEach((m) => {
      if (isSharedMenu(m)) {
        ids.add(menuKey(m));
        return;
      }
      const code = productOf(m);
      if (productState[code] && selectedMenuIds.has(menuKey(m))) {
        ids.add(menuKey(m));
      }
    });
    return [...ids];
  };

  useEffect(() => {
    loadTenantsAndPlans();
  }, [loadTenantsAndPlans]);

  useEffect(() => {
    if (tenantId) loadSubscription(tenantId, subscriptionIdParam || undefined);
  }, [tenantId, subscriptionIdParam, loadSubscription]);

  const saveEntitlements = async (subscriptionId) => {
    let updated = null;
    for (const spec of OPTIONAL_ENTITLEMENT_SPECS) {
      // Skip product-scoped fields when that product is off
      if (spec.productCode === 'HUB' && !productState.HUB) continue;
      if (spec.productCode === 'SYNC_FILE' && !productState.SYNC_FILE) continue;
      if (spec.productCode === 'AGENTIC_AI' && !productState.AGENTIC_AI) continue;

      const key = spec.formKey;
      const raw = extrasForm[key];
      if (raw === '' || raw == null) continue;
      let value;
      if (spec.type === 'BOOLEAN') {
        if (raw !== 'true' && raw !== 'false') continue;
        value = raw === 'true' ? 'TRUE' : 'FALSE';
      } else {
        value = String(raw).trim();
        if (!value) continue;
      }
      updated = await upsertEntitlement(subscriptionId, {
        productCode: spec.productCode,
        code: spec.code,
        type: spec.type,
        value,
        unit: spec.unit,
      });
    }
    return updated;
  };

  const openSendInvoiceEmail = async () => {
    if (!tenantId) {
      toast.error('Select a tenant first');
      return;
    }
    setSendBusy(true);
    setSendConfirmText('');
    setInvoiceToSend(null);
    try {
      const rows = await fetchInvoices({ tenantId });
      const list = Array.isArray(rows) ? rows : [];
      const eligible = list.filter((r) => !['DRAFT', 'VOID'].includes(String(r.invoiceStatus || '').toUpperCase()));
      if (!eligible.length) {
        toast.info('No issued invoice for this tenant yet. Save license limits first so an invoice is created.');
        return;
      }
      const sorted = [...eligible].sort((a, b) => Number(b.invoiceId) - Number(a.invoiceId));
      setInvoiceToSend(sorted[0]);
      setSendOpen(true);
    } catch (e) {
      toast.error(e.message || 'Failed to load invoice');
    } finally {
      setSendBusy(false);
    }
  };

  const confirmSendInvoiceEmail = async () => {
    if (!invoiceToSend?.invoiceId) return;
    if (sendConfirmText.trim().toLowerCase() !== 'send') {
      toast.error('Type send to confirm');
      return;
    }
    setSendBusy(true);
    try {
      await sendInvoice(invoiceToSend.invoiceId);
      toast.success(`Invoice ${invoiceToSend.invoiceNumber || invoiceToSend.invoiceId} emailed`);
      setSendOpen(false);
      setSendConfirmText('');
      setInvoiceToSend(null);
    } catch (e) {
      toast.error(e.message || 'Failed to send invoice email');
    } finally {
      setSendBusy(false);
    }
  };

  const handleSaveAll = async () => {
    if (!subscription?.subscriptionId) return;
    if (contentLocked) {
      toast.warning(subscriptionLockMessage(subscription.subscriptionStatus));
      return;
    }
    setSaving(true);
    try {
      const settingsPayload = {
        subscriptionStatus: settingsForm.subscriptionStatus,
        autoRenew: !!settingsForm.autoRenew,
        currency: settingsForm.currency,
        billingCycle: settingsForm.billingCycle,
        overagePolicy: settingsForm.overagePolicy || 'BLOCK',
        startDate: settingsForm.startDate || undefined,
        endDate: settingsForm.endDate || undefined,
      };
      if (settingsForm.gracePeriodDays !== '' && settingsForm.gracePeriodDays != null) {
        settingsPayload.gracePeriodDays = Number(settingsForm.gracePeriodDays);
      }

      await updateSubscriptionSettings(subscription.subscriptionId, settingsPayload);
      await updateSubscriptionProducts(subscription.subscriptionId, productState);
      // Note: product/feature entitlement updates may auto-assign all product menus.
      // Save the License grid selection last so unchecks win.
      await replaceLicensedConnectors(
        subscription.subscriptionId,
        [...licensedConnectorIds].map((mcId) => ({ mcId, enabled: true }))
      );
      await replaceLicensedFlows(
        subscription.subscriptionId,
        [...licensedFlowIds].map((mfId) => {
          const item = { mfId, enabled: true };
          if (
            licensedFlowUiByMfId &&
            Object.prototype.hasOwnProperty.call(licensedFlowUiByMfId, mfId)
          ) {
            const flow = (flowCatalog || []).find((f) => String(f.mfId) === String(mfId));
            const catalog = flowUiCatalogFromLookups(lookups, flow?.mfFlowFunction);
            item.screenUi = parseMasterFlowUiConfig(licensedFlowUiByMfId[mfId], catalog);
          }
          return item;
        })
      );
      await saveEntitlements(subscription.subscriptionId);

      const cleaned = (llmDraft || [])
        .filter(
          (l) =>
            (l.masterLlmId && String(l.masterLlmId).trim()) ||
            (l.modelCode && String(l.modelCode).trim())
        )
        .map((l) => ({
          ...(l.masterLlmId ? { masterLlmId: String(l.masterLlmId).trim() } : {}),
          provider: l.provider || undefined,
          modelCode: l.modelCode || undefined,
          enabled: l.enabled !== false,
          monthlyTokenLimit: l.monthlyTokenLimit || undefined,
        }));
      await replaceSubscriptionLlms(subscription.subscriptionId, cleaned);

      if (tenantId) {
        await saveTenantMenus(tenantId, buildMenuIdsToSave());
      }

      await loadSubscription(tenantId, subscription.subscriptionId);
      toast.success('License saved');
    } catch (e) {
      toast.error(e.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleProvision = async () => {
    if (!tenantId) {
      toast.warning('Select a tenant');
      return;
    }
    setSaving(true);
    try {
      const products = productOptions.filter((p) => productState[p.code]).map((p) => p.code);
      const body = {
        planName: provisionForm.planName,
        billingCycle: provisionForm.subscription,
        subscription: provisionForm.subscription,
        numberOfUsers: Number(provisionForm.numberOfUsers) || 5,
        ...(products.length ? { products } : {}),
      };
      if (!products.length) delete body.products;

      const updated = await provisionTenantLicense(tenantId, body);
      applySubscription(updated, productOptions, lookups);
      toast.success('License provisioned');
    } catch (e) {
      toast.error(e.message || 'Provision failed');
    } finally {
      setSaving(false);
    }
  };

  if (!isCdxUser()) {
    return (
      <Box p={3}>
        <Alert severity="warning">License administration is available to CDX users only.</Alert>
      </Box>
    );
  }

  return (
    <Box p={2} sx={{ width: '100%' }}>
      <Button
        startIcon={<ArrowBack />}
        onClick={() =>
          navigate(
            tenantId
              ? `/tenant?openSubscriptions=${encodeURIComponent(tenantId)}`
              : '/tenant'
          )
        }
        sx={{ mb: 1.5 }}
      >
        Back to Tenants
      </Button>
      <Box display="flex" alignItems="center" gap={1} mb={2}>
        <CardMembership color="primary" />
        <Typography variant="h5" fontWeight={600}>
          License
        </Typography>
      </Box>
      <Typography variant="body2" color="text.secondary" mb={2}>
        Products and limits for the subscription opened from <strong>Tenants → Subscriptions</strong>.
      </Typography>

      {!tenantId && (
        <Alert severity="info" sx={{ mb: 2 }}>
          No tenant selected. Open a subscription from <strong>CDX → Tenants → Subscriptions</strong>.
        </Alert>
      )}

      {tenantId && (
        <Card variant="outlined" sx={{ mb: 2 }}>
          <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
            <Box display="flex" alignItems="center" justifyContent="space-between" gap={1} flexWrap="wrap">
              <Box minWidth={0}>
                <Typography variant="subtitle1" fontWeight={600} noWrap>
                  {selectedTenant?.tenantName ||
                    selectedTenant?.companyName ||
                    `Tenant #${tenantId}`}
                  <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                    #{tenantId}
                  </Typography>
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block">
                  Schema: {selectedTenant?.tenantDbPath || '—'}
                  {selectedTenant?.tenantEmail ? ` · ${selectedTenant.tenantEmail}` : ''}
                  {subscription ? ` · Plan: ${subscription.planName}` : ''}
                  {subscription?.licenseNumber ? ` · License #: ${subscription.licenseNumber}` : ''}
                  {subscription?.subscriptionId != null ? ` · Sub ID: ${subscription.subscriptionId}` : ''}
                </Typography>
              </Box>
              <Box display="flex" gap={1} alignItems="center" flexWrap="wrap">
                {subscription && (
                  <Chip
                    size="small"
                    color={isHistorical ? 'default' : 'success'}
                    variant={isHistorical ? 'outlined' : 'filled'}
                    label={isHistorical ? 'Past subscription' : 'Current subscription'}
                  />
                )}
                <Button
                  size="small"
                  startIcon={<Refresh />}
                  variant="outlined"
                  disabled={loading}
                  onClick={() => loadSubscription(tenantId, subscriptionIdParam || undefined)}
                >
                  Refresh
                </Button>
              </Box>
            </Box>
          </CardContent>
        </Card>
      )}

      {!loading && isHistorical && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          You are viewing a past subscription. Live enforcement uses the tenant&apos;s current (latest) license.
          You can still review or correct this term&apos;s products and limits when the status is operable.
        </Alert>
      )}

      {!loading && contentLocked && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {subscriptionLockMessage(subscription?.subscriptionStatus)}
        </Alert>
      )}

      {loading && (
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress />
        </Box>
      )}

      {!loading && tenantId && !subscription && (
        <Card variant="outlined" sx={{ mb: 2 }}>
          <CardContent>
            <Alert severity="info" sx={{ mb: 2 }}>
              No subscription for this tenant. Provision a license.
            </Alert>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth size="small">
                  <InputLabel>Plan</InputLabel>
                  <Select
                    label="Plan"
                    value={provisionForm.planName}
                    onChange={(e) => setProvisionForm((f) => ({ ...f, planName: e.target.value }))}
                  >
                    {(plans.length ? plans : []).map((p) => {
                      const name = p.plan_name || p.planName;
                      return (
                        <MenuItem key={p.plan_id || p.planId || name} value={name}>
                          {name}
                        </MenuItem>
                      );
                    })}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth size="small">
                  <InputLabel>Billing</InputLabel>
                  <Select
                    label="Billing"
                    value={provisionForm.subscription}
                    onChange={(e) => setProvisionForm((f) => ({ ...f, subscription: e.target.value }))}
                  >
                    {billingOptions.map((s) => (
                      <MenuItem key={s.code} value={s.code}>
                        {s.label || s.code}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  size="small"
                  type="number"
                  label="Licensed users"
                  value={provisionForm.numberOfUsers}
                  onChange={(e) =>
                    setProvisionForm((f) => ({ ...f, numberOfUsers: e.target.value }))
                  }
                />
              </Grid>
              <Grid item xs={12}>
                {productOptions.map((p) => (
                  <FormControlLabel
                    key={p.code}
                    control={
                      <Switch
                        checked={!!productState[p.code]}
                        onChange={(e) =>
                          setProductState((s) => ({ ...s, [p.code]: e.target.checked }))
                        }
                      />
                    }
                    label={p.name}
                  />
                ))}
              </Grid>
              <Grid item xs={12}>
                <Button variant="contained" disabled={saving} onClick={handleProvision}>
                  Provision license
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}

      {!loading && subscription && (
        <>
          {/* Products expandable grid — menus live under each product (not a separate Assign menus) */}
          <Card variant="outlined" sx={{ mb: 2 }}>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between" gap={1} mb={1} flexWrap="wrap">
                <Typography variant="h6">Products</Typography>
                <Button
                  size="small"
                  startIcon={<Save />}
                  variant="contained"
                  disabled={saving || loading || contentLocked}
                  onClick={handleSaveAll}
                >
                  {saving ? 'Saving…' : 'Save'}
                </Button>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                Expand a product to see only its menus. Enable Hub → Hub menus only. Active license products
                drive role options and login sidebar after Save.
              </Typography>
              <LicenseProductsMenusGrid
                productOptions={productOptions}
                productState={productState}
                onProductChange={handleProductChange}
                menus={menus}
                selectedMenuIds={selectedMenuIds}
                onMenuToggle={handleMenuToggle}
                onToggleProductMenus={handleToggleProductMenus}
                disabled={contentLocked}
              />
            </CardContent>
          </Card>

          <Accordion
            disableGutters
            elevation={0}
            sx={{
              mb: 2,
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
              '&:before': { display: 'none' },
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{
                px: 2,
                '& .MuiAccordionSummary-content': {
                  my: 1,
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 1,
                  flexWrap: 'wrap',
                },
              }}
            >
              <Typography variant="h6">Product billing grid</Typography>
              {tenantId ? (
                <Button
                  size="small"
                  variant="outlined"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/admin/billing/subscriptions?tenantId=${tenantId}`);
                  }}
                >
                  Open in Billing
                </Button>
              ) : null}
            </AccordionSummary>
            <AccordionDetails sx={{ px: 2, pt: 0, pb: 2 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                Main products and sub-products (storage GB, connectors, flows, agents, tokens…) with
                quantity, unit price, and cost. Access is controlled above; costs come from Product
                Master + pricing / subscription charges. Full edit under Billing → Subscriptions.
              </Typography>
              <ProductCostGrid grid={productCostGrid} dense embedded />
            </AccordionDetails>
          </Accordion>

          {/* License configuration — Integration includes Add from Master Connector / Flow */}
          <Card variant="outlined" sx={{ mb: 2 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                License configuration
              </Typography>
              <OptionalExtrasFields
                extras={extrasForm}
                setExtra={(k, v) => setExtrasForm((f) => ({ ...f, [k]: v }))}
                mode="license"
                enabledProducts={productState}
                featureFlagOptions={featureFlagOptions}
                disabled={contentLocked}
                integrationAssets={
                  <LicenseIntegrationAssets
                    hubEnabled={!!productState.HUB}
                    connectorCatalog={connectorCatalog}
                    flowCatalog={flowCatalog}
                    licensedConnectorIds={licensedConnectorIds}
                    licensedFlowIds={licensedFlowIds}
                    licensedFlowUiByMfId={licensedFlowUiByMfId}
                    flowUiLookups={lookups}
                    onChangeConnectors={setLicensedConnectorIds}
                    onChangeFlows={setLicensedFlowIds}
                    onChangeFlowUi={setLicensedFlowUiByMfId}
                    maxConnectors={extrasForm.maxConnectors}
                    maxFlows={extrasForm.maxFlows}
                    readOnly={contentLocked}
                  />
                }
              />
            </CardContent>
          </Card>

          {/* Usage + LLMs */}
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} md={6}>
              <Card variant="outlined" sx={{ height: '100%' }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Usage
                  </Typography>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Meter</TableCell>
                        <TableCell align="right">Used / limit</TableCell>
                        <TableCell>Unit</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {usageRows.map((u) => (
                        <TableRow key={u.usageType}>
                          <TableCell>
                            {USAGE_TYPE_LABELS[u.usageType] || u.usageType}
                          </TableCell>
                          <TableCell align="right">
                            {formatQty(u.usedQuantity)} / {formatQty(u.allocatedQuantity)}
                          </TableCell>
                          <TableCell>{u.unit || '—'}</TableCell>
                        </TableRow>
                      ))}
                      {!usageRows.length && (
                        <TableRow>
                          <TableCell colSpan={3}>No usage yet</TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card variant="outlined" sx={{ height: '100%' }}>
                <CardContent>
                  <LicenseLlmAssets
                    agenticEnabled={!!productState.AGENTIC_AI}
                    llmCatalog={llmCatalog}
                    llmDraft={llmDraft}
                    onChange={setLlmDraft}
                    readOnly={contentLocked}
                  />
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          <Box display="flex" justifyContent="flex-end" gap={1} mb={2} flexWrap="wrap">
            <Button
              startIcon={<MailOutline />}
              variant="outlined"
              disabled={saving || loading || sendBusy || !tenantId}
              onClick={openSendInvoiceEmail}
            >
              Send invoice email
            </Button>
            <Button
              startIcon={<Save />}
              variant="contained"
              disabled={saving || loading || contentLocked}
              onClick={handleSaveAll}
            >
              {saving ? 'Saving…' : 'Save license'}
            </Button>
          </Box>
        </>
      )}

      <Dialog open={sendOpen} onClose={() => !sendBusy && setSendOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Send invoice email</DialogTitle>
        <DialogContent dividers>
          {invoiceToSend ? (
            <>
              <Typography variant="body2" sx={{ mb: 1 }}>
                Invoice <strong>{invoiceToSend.invoiceNumber}</strong>
                {' · '}
                {formatMoney(invoiceToSend.totalAmount, invoiceToSend.currency)}
                {' · '}
                {invoiceToSend.invoiceStatus}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                This emails the billing contact. Check amounts first. Type <strong>send</strong> below to confirm.
              </Typography>
              <TextField
                fullWidth
                size="small"
                autoFocus
                label='Type "send" to confirm'
                value={sendConfirmText}
                onChange={(e) => setSendConfirmText(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') confirmSendInvoiceEmail();
                }}
              />
            </>
          ) : (
            <Typography variant="body2">Loading invoice…</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button disabled={sendBusy} onClick={() => setSendOpen(false)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={sendBusy || sendConfirmText.trim().toLowerCase() !== 'send' || !invoiceToSend}
            onClick={confirmSendInvoiceEmail}
          >
            {sendBusy ? 'Sending…' : 'Send email'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default LicenseAdmin;
